import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  TextField,
} from "@mui/material";
import { Save, Close, FormatIndentIncrease } from "@mui/icons-material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { toast } from "react-toastify";
import PageTitle from "../../widgets/PageTitle";
import { getHeader } from "../../utils/util";

const JsonTextEditor = ({ initialData = {}, apiEndpoint, onSuccess }) => {
  const [jsonText, setJsonText] = useState(
    JSON.stringify(initialData, null, 2)
  );
  const [isValidJson, setIsValidJson] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setJsonText(newText);

    try {
      JSON.parse(newText);
      setIsValidJson(true);
    } catch (err) {
      setIsValidJson(false);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setIsValidJson(true);
    } catch (err) {
      setIsValidJson(false);
      setError("Invalid JSON - cannot format");
    }
  };

  const handleSubmit = async () => {
    if (!isValidJson) {
      setError("Please fix JSON errors before submitting");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const parsedData = JSON.parse(jsonText);
      const response = await fetch(apiEndpoint, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify(parsedData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(true);
      if (onSuccess) onSuccess(result);
    } catch (err) {
      setError(err.message || "Failed to submit JSON data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <PageTitle>JSON Text Editor</PageTitle>

        <Box sx={{ mb: 2, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FormatIndentIncrease />}
            onClick={formatJson}
            disabled={!jsonText}
          >
            Format JSON
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              if (isValidJson) {
                setShowPreview(!showPreview);
              } else {
                toast.error("Please ensure valid json!");
              }
            }}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        </Box>

        {!showPreview && (
          <TextField
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            value={jsonText}
            onChange={handleTextChange}
            error={!isValidJson}
            helperText={!isValidJson ? "Invalid JSON syntax" : ""}
            variant="outlined"
            sx={{
              mb: 2,
              "& textarea": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />
        )}

        {showPreview && (
          <Box
            sx={{
              mb: 3,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <SyntaxHighlighter
              language="json"
              style={atomDark}
              customStyle={{
                margin: 0,
                borderRadius: 0,
                fontSize: "0.875rem",
              }}
            >
              {jsonText}
            </SyntaxHighlighter>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={handleSubmit}
            disabled={isLoading || !isValidJson}
          >
            {isLoading ? "Submitting..." : "Submit JSON"}
          </Button>
        </Box>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseAlert}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseAlert}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          JSON submitted successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default JsonTextEditor;
